import React from 'react';
import { AdminPushReachabilityDto, getAdminPushReachability, useApiClient } from '../api/client';
import { useI18n } from '../i18n/I18nProvider';
import { ActivityChart } from './AnalyticsActivityChart';
import { MetricCard } from './ui';

export const PushReachabilityPanel: React.FC<{ days: number | 'all' }> = ({ days }) => {
  const { request } = useApiClient();
  const { t, language } = useI18n();
  const [data, setData] = React.useState<AdminPushReachabilityDto | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);
  const [refresh, setRefresh] = React.useState(0);
  const [historyMetric, setHistoryMetric] = React.useState<'rate' | 'count'>('rate');

  React.useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    // The snapshot calendar is always Europe/Vienna, independently of the browser's time zone.
    const today = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/Vienna', year: 'numeric', month: '2-digit', day: '2-digit',
    }).formatToParts(new Date());
    const part = (type: string) => today.find((item) => item.type === type)!.value;
    const to = `${part('year')}-${part('month')}-${part('day')}`;
    const start = new Date(`${to}T12:00:00Z`);
    if (days !== 'all') start.setUTCDate(start.getUTCDate() - days + 1);
    const query = days === 'all' ? { allTime: true as const } : { from: start.toISOString().slice(0, 10), to };
    getAdminPushReachability(request, query)
      .then((result) => { if (active) setData(result); })
      .catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [request, days, refresh]);

  const number = (value: number) => new Intl.NumberFormat(language).format(value);
  const percent = (registered: number, users: number) => users ? `${(registered / users * 100).toFixed(1)}%` : '—';
  const audience = (days: number) => days === 0 ? t('analytics.push.allUsers') : t('analytics.push.activeDays', { days });
  const selectedDays = days === 'all' ? 0 : days;
  const current = data?.windows.find((window) => window.days === selectedDays);
  const history = data?.history.filter((sample) => sample.metrics.days === selectedDays) ?? [];
  const samples = new Map(history.map((sample) => [sample.date, sample.metrics]));
  const recordedDates = history.map((sample) => sample.date).sort();
  const dates: string[] = [];
  if (recordedDates.length) {
    const day = new Date(`${recordedDates[0]}T12:00:00Z`);
    const last = recordedDates[recordedDates.length - 1];
    while (day.toISOString().slice(0, 10) <= last) {
      dates.push(day.toISOString().slice(0, 10));
      day.setUTCDate(day.getUTCDate() + 1);
    }
  }

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm" aria-label={t('analytics.push.title')}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <h2 className="text-lg font-bold text-gray-900">{t('analytics.push.title')}</h2>
          <p className="mt-1 text-sm text-gray-500">{t('analytics.push.description')}</p>
        </div>
        <button type="button" onClick={() => setRefresh((value) => value + 1)} disabled={loading}
          className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 disabled:opacity-50">
          {t('analytics.push.refresh')}
        </button>
      </div>
      {loading && <p className="py-6 text-sm text-gray-500">{t('analytics.loading')}</p>}
      {!loading && error && <p role="alert" className="py-6 text-sm text-red-700">{t('analytics.push.error')}</p>}
      {!loading && !error && data && <>
        <p className="mt-3 text-xs text-gray-500">{t('analytics.push.updated', { date: new Date(data.generatedAt).toLocaleString(language, { timeZone: data.timeZone }) })}</p>
        {current && <>
          <h3 className="mb-3 mt-5 font-semibold text-gray-900">{audience(selectedDays)}</h3>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" role="group" aria-label={audience(selectedDays)}>
            <MetricCard label={t('analytics.push.users')} value={number(current.users)} tone="slate" />
            <MetricCard label={t('analytics.push.registered')} value={number(current.registeredUsers)} tone="green" />
            <MetricCard label={t('analytics.push.missing')} value={number(current.users - current.registeredUsers)} tone="amber" />
            <MetricCard label={t('analytics.push.coverage')} value={percent(current.registeredUsers, current.users)} tone="violet" />
          </div>
          <dl className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-sm text-gray-600">
            {([['iOS', current.iosUsers], ['Android', current.androidUsers], [t('analytics.push.unknown'), current.unknownPlatformUsers]] as const).map(([label, count]) =>
              <div key={label} className="flex gap-2"><dt>{label}</dt><dd className="font-semibold text-gray-900">{number(count)}</dd></div>)}
          </dl>
        </>}
        <p className="mt-3 text-xs leading-5 text-gray-500">{t('analytics.push.platformHint')}</p>
        <div className="mt-6 border-t border-gray-100 pt-5">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <h3 className="font-bold text-gray-900">{t('analytics.push.history')}</h3>
            <div className="inline-flex gap-1" aria-label={t('analytics.push.historyMetric')}>
              {(['rate', 'count'] as const).map((metric) => <button key={metric} type="button" aria-pressed={historyMetric === metric}
                onClick={() => setHistoryMetric(metric)}
                className={`rounded-md px-3 py-1.5 text-sm font-semibold ${historyMetric === metric ? 'bg-secondary text-white' : 'bg-gray-100 text-gray-700'}`}>
                {t(`analytics.push.${metric}`)}
              </button>)}
            </div>
          </div>
          <p className="mb-4 text-xs leading-5 text-gray-500">{data.trackingSince
            ? t('analytics.push.trackingSince', { date: data.trackingSince }) : t('analytics.push.pending')}</p>
          {dates.length === 0 ? <p className="py-5 text-sm text-gray-500">{t('analytics.push.noHistory')}</p> : <>
            <ActivityChart labels={dates} ariaLabel={t('analytics.push.chart')}
              maximum={historyMetric === 'rate' ? 100 : undefined} valueSuffix={historyMetric === 'rate' ? '%' : ''}
              series={[{
                label: audience(selectedDays), color: '#7c3aed', values: dates.map((date) => {
                  const sample = samples.get(date);
                  if (!sample) return null;
                  return historyMetric === 'count' ? sample.registeredUsers : sample.users ? sample.registeredUsers / sample.users * 100 : null;
                }),
              }]} />
            <details className="mt-3 text-sm">
              <summary className="cursor-pointer font-semibold text-gray-700">{t('analytics.push.historyDetails')}</summary>
              <div className="mt-3 max-h-80 overflow-auto">
                <table className="w-full text-left text-xs">
                  <thead><tr>{[t('analytics.push.recordedAt'), t('analytics.push.registered'), t('analytics.push.coverage')].map((label) =>
                    <th className="px-3 py-2" key={label}>{label}</th>)}</tr></thead>
                  <tbody>{history.map((sample) => <tr key={`${sample.date}-${sample.metrics.days}`}>
                    <td className="whitespace-nowrap px-3 py-2"><time dateTime={sample.capturedAt}>{new Date(sample.capturedAt).toLocaleString(language, { timeZone: data.timeZone })}</time></td>
                    <td className="px-3 py-2">{number(sample.metrics.registeredUsers)} / {number(sample.metrics.users)}</td>
                    <td className="px-3 py-2">{percent(sample.metrics.registeredUsers, sample.metrics.users)}</td>
                  </tr>)}</tbody>
                </table>
              </div>
            </details>
          </>}
        </div>
      </>}
    </section>
  );
};
